import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/App";
import { 
  User, 
  GraduationCap, 
  Briefcase, 
  FileText, 
  Send, 
  CheckCircle2,
  X,
  Lightbulb,
  AlertCircle,
  Clock,
  BookOpen
} from "lucide-react";

const stepOnboardingContent = {
  "Personal Information": {
    icon: User, iconColor: "#00677F",
    title: "Personal Information", subtitle: "Tell us about yourself",
    tips: [
      "Fill out your legal name as it appears on official documents",
      "Provide accurate contact information - we'll use this to reach you",
      "Your address will be used for sending important materials",
      "All fields marked with * are required"
    ],
    reminder: "Your progress is automatically saved as you complete each section."
  },
  "Academic History": {
    icon: GraduationCap, iconColor: "#7B68EE",
    title: "Academic History", subtitle: "Share your educational background",
    tips: [
      "Add all colleges/universities you've attended",
      "Include your prerequisites and track completion status",
      "TOEFL scores are required for international students",
      "Check Course Equivalency if unsure about prerequisites"
    ],
    reminder: "All post-secondary official transcripts must be submitted, regardless of whether they contain applicable courses."
  },
  "Employment History": {
    icon: Briefcase, iconColor: "#DC5034",
    title: "Employment History", subtitle: "Share your work experience",
    tips: [
      "List your most recent employment first",
      "Include healthcare-related experience if applicable",
      "Volunteer work and internships count too!",
      "Provide supervisor contact info for verification"
    ],
    reminder: "Employment history helps us understand your background and commitment to healthcare."
  },
  "Documents Required": {
    icon: FileText, iconColor: "#739600",
    title: "Documents Required", subtitle: "Upload your supporting documents",
    tips: [
      "Upload unofficial transcripts from each institution",
      "Accepted formats: PDF, JPG, PNG (max 10MB each)",
      "You can upload multiple files per document type",
      "Ensure documents are clear and readable"
    ],
    reminder: "Official transcripts will be requested in the next step."
  },
  "Official Transcripts": {
    icon: Send, iconColor: "#00677F",
    title: "Request Official Transcripts", subtitle: "Verify your transcript requests",
    tips: [
      "Request official transcripts directly from each institution",
      "Mark each institution as 'Requested' after you've ordered",
      "Enter the date you submitted each request",
      "Transcripts should be sent directly to USA.edu"
    ],
    reminder: "Processing times vary by institution. Request early to avoid delays!"
  },
  "Recommendation": {
    icon: BookOpen, iconColor: "#E91E63",
    title: "Recommendation", subtitle: "Request your letters of recommendation",
    tips: [
      "Add your recommender's details and send the request",
      "Recommenders will receive a link to complete an online form",
      "Track the status of each recommendation from this page",
      "Professional references only: Clinical, Academic, or Supervisor"
    ],
    reminder: "Follow up with recommenders if the status hasn't changed after a few days."
  },
  "Review & Submit": {
    icon: CheckCircle2, iconColor: "#739600",
    title: "Review & Submit", subtitle: "Final review before submission",
    tips: [
      "Review all sections for accuracy",
      "Ensure all required documents are uploaded",
      "Verify transcript requests are marked complete",
      "Once submitted, changes cannot be made"
    ],
    reminder: "Take your time to review everything carefully before submitting."
  }
};

const StepOnboardingModal = ({ isOpen, step, stepLabel, appId, onComplete }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const content = stepLabel ? stepOnboardingContent[stepLabel] : null;
  
  if (!isOpen || !content) return null;

  const handleComplete = () => {
    if (appId && stepLabel) {
      localStorage.setItem(`step_onboarding_${appId}_${stepLabel}`, "true");
    }
    onComplete?.();
  };

  const Icon = content.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className={`absolute inset-0 ${isDark ? 'bg-black/80' : 'bg-black/40'} backdrop-blur-sm`} onClick={handleComplete} />
      
      <div className={`relative rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl animate-[fadeSlideUp_0.4s_ease-out] ${isDark ? 'bg-[#11161F] border border-white/10' : 'bg-white border border-gray-200'}`}>
        <button
          onClick={handleComplete}
          className={`absolute top-4 right-4 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 pb-0">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: `${content.iconColor}15` }}
          >
            <Icon className="w-7 h-7" style={{ color: content.iconColor }} />
          </div>
          
          <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.title}</h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{content.subtitle}</p>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-[#C9DD03]" />
            <span className="text-sm font-medium text-[#C9DD03]">Quick Tips</span>
          </div>
          
          <ul className="space-y-2.5">
            {content.tips.map((tip, index) => (
              <li key={index} className={`flex items-start gap-3 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                <CheckCircle2 className="w-4 h-4 text-[#739600] mt-0.5 flex-shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>

          <div className={`mt-4 p-3 rounded-xl ${isDark ? 'bg-[#00677F]/10 border border-[#00677F]/20' : 'bg-[#00677F]/5 border border-[#00677F]/15'}`}>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#00677F] mt-0.5 flex-shrink-0" />
              <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{content.reminder}</p>
            </div>
          </div>
        </div>

        <div className="p-6 pt-0">
          <Button
            onClick={handleComplete}
            className="w-full h-11 rounded-xl font-medium transition-all text-white"
            style={{ backgroundColor: content.iconColor }}
          >
            Got it, let's go!
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepOnboardingModal;
